import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { format, parseISO } from 'date-fns';
import {
  AlertTriangle, ThumbsUp, Plus, Edit3, Trash2, X, Check, Search,
  Phone, Mail, User, Globe, MessageSquare, ChevronDown, ChevronUp,
  Star, CheckCircle, Clock, Filter
} from 'lucide-react';
import { formatFullName } from '../utils/helpers';
import './ComplaintsFeedback.css';

const COMPLAINT_SOURCES = [
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'in_person', label: 'In Person', icon: User },
  { value: 'google_review', label: 'Google Review', icon: Globe },
  { value: 'text', label: 'Text/SMS', icon: MessageSquare },
];

const FEEDBACK_SOURCES = [
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'in_person', label: 'In Person', icon: User },
  { value: 'google_review', label: 'Google Review', icon: Globe },
];

const COMPLAINT_STATUSES = [
  { value: 'open', label: 'Open', color: '#ef4444' },
  { value: 'in_progress', label: 'In Progress', color: '#f59e0b' },
  { value: 'resolved', label: 'Resolved', color: '#10b981' },
];

function ComplaintsFeedback() {
  const {
    state, addComplaint, updateComplaint, deleteComplaint,
    addFeedback, updateFeedback, deleteFeedback
  } = useApp();
  const { clients, employees, jobs, complaints, feedbacks } = state;

  const [activeTab, setActiveTab] = useState('complaints');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form state for complaints
  const emptyComplaint = {
    clientId: '', jobId: '', employeeIds: [], date: format(new Date(), 'yyyy-MM-dd'),
    source: 'phone', description: '', remedy: '', status: 'open'
  };
  const emptyFeedback = {
    clientId: '', jobId: '', employeeIds: [], date: format(new Date(), 'yyyy-MM-dd'),
    source: 'phone', description: '', acknowledged: false
  };

  const [formData, setFormData] = useState(emptyComplaint);

  const activeEmployees = useMemo(() =>
    employees.filter(e => e.status === 'active').sort((a, b) =>
      `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
    ), [employees]);

  const activeClients = useMemo(() =>
    clients.filter(c => c.status !== 'archived').sort((a, b) =>
      formatFullName(a).localeCompare(formatFullName(b))
    ), [clients]);

  // Client jobs for selected client
  const clientJobs = useMemo(() => {
    if (!formData.clientId) return [];
    return jobs.filter(j => j.clientId === formData.clientId)
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
      .slice(0, 20);
  }, [formData.clientId, jobs]);

  // Filtered list
  const filteredItems = useMemo(() => {
    const list = activeTab === 'complaints' ? (complaints || []) : (feedbacks || []);
    return list.filter(item => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const client = clients.find(c => c.id === item.clientId);
        const clientName = client ? formatFullName(client).toLowerCase() : '';
        const desc = (item.description || '').toLowerCase();
        if (!clientName.includes(q) && !desc.includes(q)) return false;
      }
      if (activeTab === 'complaints' && statusFilter !== 'all' && item.status !== statusFilter) return false;
      return true;
    }).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  }, [activeTab, complaints, feedbacks, searchTerm, statusFilter, clients]);

  const getClientName = (clientId) => {
    const c = clients.find(c => c.id === clientId);
    return c ? formatFullName(c) : 'Unknown Client';
  };

  const getEmployeeNames = (empIds) => {
    if (!empIds?.length) return 'None';
    return empIds.map(id => {
      const e = employees.find(e => e.id === id);
      return e ? `${e.firstName} ${e.lastName}` : 'Unknown';
    }).join(', ');
  };

  const getJobLabel = (jobId) => {
    if (!jobId) return 'No job linked';
    const j = jobs.find(j => j.id === jobId);
    if (!j) return jobId;
    const client = clients.find(c => c.id === j.clientId);
    const name = client ? formatFullName(client) : '';
    return `${j.date || 'No date'} - ${name}`;
  };

  const getSourceIcon = (source) => {
    const s = COMPLAINT_SOURCES.find(cs => cs.value === source);
    if (!s) return <MessageSquare size={14} />;
    const Icon = s.icon;
    return <Icon size={14} />;
  };

  const getSourceLabel = (source) => {
    const s = COMPLAINT_SOURCES.find(cs => cs.value === source);
    return s ? s.label : source;
  };

  const openNewForm = () => {
    setFormData(activeTab === 'complaints' ? { ...emptyComplaint } : { ...emptyFeedback });
    setEditingItem(null);
    setShowForm(true);
  };

  const openEditForm = (item) => {
    setFormData({
      clientId: item.clientId || '',
      jobId: item.jobId || '',
      employeeIds: item.employeeIds || [],
      date: item.date || format(new Date(), 'yyyy-MM-dd'),
      source: item.source || 'phone',
      description: item.description || '',
      ...(activeTab === 'complaints'
        ? { remedy: item.remedy || '', status: item.status || 'open' }
        : { acknowledged: item.acknowledged || false })
    });
    setEditingItem(item);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.description.trim()) return;

    if (activeTab === 'complaints') {
      if (editingItem) {
        await updateComplaint(editingItem.id, formData);
      } else {
        await addComplaint(formData);
      }
    } else {
      if (editingItem) {
        await updateFeedback(editingItem.id, formData);
      } else {
        await addFeedback(formData);
      }
    }
    setShowForm(false);
    setEditingItem(null);
  };

  const handleDelete = async (id) => {
    if (activeTab === 'complaints') {
      await deleteComplaint(id);
    } else {
      await deleteFeedback(id);
    }
    setDeleteConfirm(null);
  };

  const toggleEmployee = (empId) => {
    setFormData(prev => ({
      ...prev,
      employeeIds: prev.employeeIds.includes(empId)
        ? prev.employeeIds.filter(id => id !== empId)
        : [...prev.employeeIds, empId]
    }));
  };

  // Stats
  const complaintStats = useMemo(() => {
    const all = complaints || [];
    return {
      total: all.length,
      open: all.filter(c => c.status === 'open').length,
      inProgress: all.filter(c => c.status === 'in_progress').length,
      resolved: all.filter(c => c.status === 'resolved').length,
    };
  }, [complaints]);

  const feedbackStats = useMemo(() => {
    const all = feedbacks || [];
    return {
      total: all.length,
      acknowledged: all.filter(f => f.acknowledged).length,
    };
  }, [feedbacks]);

  return (
    <div className="cf-page">
      {/* Header */}
      <div className="cf-header">
        <div>
          <h1>Complaints & Feedback</h1>
          <p className="cf-header-sub">Track complaints, positive feedback, and employee recognition</p>
        </div>
        <button className="cf-add-btn" onClick={openNewForm}>
          <Plus size={16} /> {activeTab === 'complaints' ? 'Log Complaint' : 'Log Feedback'}
        </button>
      </div>

      {/* Tabs */}
      <div className="cf-tabs">
        <button
          className={`cf-tab ${activeTab === 'complaints' ? 'active' : ''}`}
          onClick={() => { setActiveTab('complaints'); setSearchTerm(''); setStatusFilter('all'); }}
        >
          <AlertTriangle size={16} />
          Complaints
          {complaintStats.open > 0 && <span className="cf-tab-badge red">{complaintStats.open}</span>}
        </button>
        <button
          className={`cf-tab ${activeTab === 'feedback' ? 'active' : ''}`}
          onClick={() => { setActiveTab('feedback'); setSearchTerm(''); setStatusFilter('all'); }}
        >
          <ThumbsUp size={16} />
          Positive Feedback
          {feedbackStats.total > 0 && <span className="cf-tab-badge green">{feedbackStats.total}</span>}
        </button>
      </div>

      {/* Stats */}
      {activeTab === 'complaints' ? (
        <div className="cf-stats-row">
          <div className="cf-stat">
            <span className="cf-stat-value red">{complaintStats.open}</span>
            <span className="cf-stat-label">Open</span>
          </div>
          <div className="cf-stat">
            <span className="cf-stat-value gold">{complaintStats.inProgress}</span>
            <span className="cf-stat-label">In Progress</span>
          </div>
          <div className="cf-stat">
            <span className="cf-stat-value green">{complaintStats.resolved}</span>
            <span className="cf-stat-label">Resolved</span>
          </div>
          <div className="cf-stat">
            <span className="cf-stat-value">{complaintStats.total}</span>
            <span className="cf-stat-label">Total</span>
          </div>
        </div>
      ) : (
        <div className="cf-stats-row">
          <div className="cf-stat">
            <span className="cf-stat-value green">{feedbackStats.total}</span>
            <span className="cf-stat-label">Total Feedback</span>
          </div>
          <div className="cf-stat">
            <span className="cf-stat-value">{feedbackStats.acknowledged}</span>
            <span className="cf-stat-label">Acknowledged</span>
          </div>
        </div>
      )}

      {/* Search + Filters */}
      <div className="cf-filters">
        <div className="cf-search">
          <Search size={16} />
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder={`Search ${activeTab}...`}
          />
        </div>
        {activeTab === 'complaints' && (
          <div className="cf-status-filters">
            {['all', 'open', 'in_progress', 'resolved'].map(s => (
              <button
                key={s}
                className={`cf-filter-pill ${statusFilter === s ? 'active' : ''}`}
                onClick={() => setStatusFilter(s)}
              >
                {s === 'all' ? 'All' : s === 'in_progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* List */}
      <div className="cf-list">
        {filteredItems.length === 0 && (
          <div className="cf-empty">
            {activeTab === 'complaints'
              ? <><AlertTriangle size={32} /><p>No complaints recorded yet</p></>
              : <><ThumbsUp size={32} /><p>No positive feedback recorded yet</p></>
            }
          </div>
        )}
        {filteredItems.map(item => {
          const isExpanded = expandedId === item.id;
          return (
            <div key={item.id} className={`cf-item ${activeTab === 'complaints' ? `status-${item.status}` : (item.acknowledged ? 'acknowledged' : '')}`}>
              <div className="cf-item-header" onClick={() => setExpandedId(isExpanded ? null : item.id)}>
                <div className="cf-item-left">
                  <div className="cf-item-icon">
                    {activeTab === 'complaints'
                      ? <AlertTriangle size={16} />
                      : <Star size={16} />
                    }
                  </div>
                  <div className="cf-item-summary">
                    <div className="cf-item-top-row">
                      <span className="cf-item-client">{getClientName(item.clientId)}</span>
                      <span className="cf-item-source">{getSourceIcon(item.source)} {getSourceLabel(item.source)}</span>
                      <span className="cf-item-date">{item.date ? format(parseISO(item.date), 'MMM d, yyyy') : ''}</span>
                    </div>
                    <p className="cf-item-desc">{item.description?.length > 120 ? item.description.slice(0, 120) + '...' : item.description}</p>
                    {item.employeeIds?.length > 0 && (
                      <div className="cf-item-employees">
                        <User size={12} /> {getEmployeeNames(item.employeeIds)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="cf-item-right">
                  {activeTab === 'complaints' && (
                    <span className={`cf-status-badge ${item.status}`}>
                      {item.status === 'open' ? 'Open' : item.status === 'in_progress' ? 'In Progress' : 'Resolved'}
                    </span>
                  )}
                  {activeTab === 'feedback' && item.acknowledged && (
                    <span className="cf-acknowledged-badge"><CheckCircle size={14} /> Acknowledged</span>
                  )}
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>
              {isExpanded && (
                <div className="cf-item-detail">
                  <div className="cf-detail-grid">
                    <div className="cf-detail-field">
                      <label>Client</label>
                      <span>{getClientName(item.clientId)}</span>
                    </div>
                    <div className="cf-detail-field">
                      <label>Date</label>
                      <span>{item.date ? format(parseISO(item.date), 'EEEE, MMM d, yyyy') : 'N/A'}</span>
                    </div>
                    <div className="cf-detail-field">
                      <label>Source</label>
                      <span>{getSourceIcon(item.source)} {getSourceLabel(item.source)}</span>
                    </div>
                    <div className="cf-detail-field">
                      <label>Job</label>
                      <span>{getJobLabel(item.jobId)}</span>
                    </div>
                    <div className="cf-detail-field">
                      <label>Employee(s)</label>
                      <span>{getEmployeeNames(item.employeeIds)}</span>
                    </div>
                    {activeTab === 'complaints' && (
                      <div className="cf-detail-field">
                        <label>Status</label>
                        <span className={`cf-status-badge ${item.status}`}>
                          {item.status === 'open' ? 'Open' : item.status === 'in_progress' ? 'In Progress' : 'Resolved'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="cf-detail-full">
                    <label>Description</label>
                    <p>{item.description}</p>
                  </div>
                  {activeTab === 'complaints' && item.remedy && (
                    <div className="cf-detail-full">
                      <label>Remedy</label>
                      <p>{item.remedy}</p>
                    </div>
                  )}
                  <div className="cf-item-actions">
                    <button className="cf-action-btn edit" onClick={(e) => { e.stopPropagation(); openEditForm(item); }}>
                      <Edit3 size={14} /> Edit
                    </button>
                    {activeTab === 'complaints' && item.status !== 'resolved' && (
                      <button className="cf-action-btn resolve" onClick={async (e) => {
                        e.stopPropagation();
                        await updateComplaint(item.id, { status: 'resolved' });
                      }}>
                        <Check size={14} /> Mark Resolved
                      </button>
                    )}
                    {activeTab === 'feedback' && !item.acknowledged && (
                      <button className="cf-action-btn resolve" onClick={async (e) => {
                        e.stopPropagation();
                        await updateFeedback(item.id, { acknowledged: true });
                      }}>
                        <CheckCircle size={14} /> Acknowledge
                      </button>
                    )}
                    {deleteConfirm === item.id ? (
                      <div className="cf-delete-confirm">
                        <span>Delete?</span>
                        <button className="cf-action-btn danger" onClick={() => handleDelete(item.id)}><Check size={14} /> Yes</button>
                        <button className="cf-action-btn" onClick={() => setDeleteConfirm(null)}><X size={14} /> No</button>
                      </div>
                    ) : (
                      <button className="cf-action-btn danger" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(item.id); }}>
                        <Trash2 size={14} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="cf-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="cf-modal" onClick={e => e.stopPropagation()}>
            <div className="cf-modal-header">
              <h2>{editingItem ? 'Edit' : 'Log'} {activeTab === 'complaints' ? 'Complaint' : 'Positive Feedback'}</h2>
              <button className="cf-modal-close" onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <div className="cf-modal-body">
              {/* Date */}
              <div className="cf-form-row">
                <label>Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={e => setFormData(p => ({ ...p, date: e.target.value }))}
                />
              </div>

              {/* Client */}
              <div className="cf-form-row">
                <label>Client</label>
                <select
                  value={formData.clientId}
                  onChange={e => setFormData(p => ({ ...p, clientId: e.target.value, jobId: '' }))}
                >
                  <option value="">Select client...</option>
                  {activeClients.map(c => (
                    <option key={c.id} value={c.id}>{formatFullName(c)}</option>
                  ))}
                </select>
              </div>

              {/* Job */}
              {formData.clientId && clientJobs.length > 0 && (
                <div className="cf-form-row">
                  <label>Linked Job (optional)</label>
                  <select
                    value={formData.jobId}
                    onChange={e => setFormData(p => ({ ...p, jobId: e.target.value }))}
                  >
                    <option value="">No specific job</option>
                    {clientJobs.map(j => (
                      <option key={j.id} value={j.id}>
                        {j.date || 'No date'} - {j.status} {j.price ? `($${j.price})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Source */}
              <div className="cf-form-row">
                <label>How did this come in?</label>
                <div className="cf-source-pills">
                  {(activeTab === 'complaints' ? COMPLAINT_SOURCES : FEEDBACK_SOURCES).map(s => {
                    const Icon = s.icon;
                    return (
                      <button
                        key={s.value}
                        className={`cf-source-pill ${formData.source === s.value ? 'active' : ''}`}
                        onClick={() => setFormData(p => ({ ...p, source: s.value }))}
                      >
                        <Icon size={14} /> {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Employee(s) */}
              <div className="cf-form-row">
                <label>Employee(s) {activeTab === 'complaints' ? 'involved' : 'to acknowledge'}</label>
                <div className="cf-employee-pills">
                  {activeEmployees.map(e => (
                    <button
                      key={e.id}
                      className={`cf-emp-pill ${formData.employeeIds.includes(e.id) ? 'selected' : ''}`}
                      onClick={() => toggleEmployee(e.id)}
                    >
                      {formData.employeeIds.includes(e.id) && <Check size={12} />}
                      {e.firstName} {e.lastName?.[0]}.
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="cf-form-row">
                <label>{activeTab === 'complaints' ? 'Description of Complaint' : 'Feedback Details'}</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                  placeholder={activeTab === 'complaints'
                    ? 'Describe the complaint, what happened, and any relevant details...'
                    : 'Describe the positive feedback received...'}
                  rows={4}
                />
              </div>

              {/* Remedy (complaints only) */}
              {activeTab === 'complaints' && (
                <div className="cf-form-row">
                  <label>Remedy / Resolution</label>
                  <textarea
                    value={formData.remedy || ''}
                    onChange={e => setFormData(p => ({ ...p, remedy: e.target.value }))}
                    placeholder="What was done or needs to be done to resolve this..."
                    rows={3}
                  />
                </div>
              )}

              {/* Status (complaints only) */}
              {activeTab === 'complaints' && (
                <div className="cf-form-row">
                  <label>Status</label>
                  <div className="cf-source-pills">
                    {COMPLAINT_STATUSES.map(s => (
                      <button
                        key={s.value}
                        className={`cf-source-pill ${formData.status === s.value ? 'active' : ''}`}
                        style={formData.status === s.value ? { borderColor: s.color, color: s.color } : {}}
                        onClick={() => setFormData(p => ({ ...p, status: s.value }))}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="cf-modal-footer">
              <button className="cf-cancel-btn" onClick={() => setShowForm(false)}>Cancel</button>
              <button
                className="cf-submit-btn"
                onClick={handleSubmit}
                disabled={!formData.description.trim()}
              >
                {editingItem ? 'Save Changes' : activeTab === 'complaints' ? 'Log Complaint' : 'Log Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ComplaintsFeedback;
